// GLSL ES 3.00 for the map layer and the optimistic-click overlay.
//
// Two programs, both drawing from the same camera uniforms so that a pixel and
// the marker over it can never disagree about where they are.
//
// The map is ONE full-viewport triangle. There is no geometry for the board: the
// fragment shader inverts the camera to find which board pixel a fragment lands
// on and fetches it. That is what makes pan and zoom free — they are two uniforms
// — and it is also what makes the sampling exactly nearest-neighbour, because
// `texelFetch` takes integer texel coordinates and does no filtering at all. A
// pixel game that bilinear-filters its pixels is wrong, and the way to be sure is
// to never give the hardware a filter to apply.

/** The full-viewport triangle. No attributes: gl_VertexID is enough. */
export const MAP_VERT = `#version 300 es
precision highp float;
out vec2 vClip;
void main() {
  // One oversized triangle rather than two triangles of a quad: no shared edge,
  // so no chance of a seam and one fewer primitive to rasterise.
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  vClip = p * 2.0 - 1.0;
  gl_Position = vec4(vClip, 0.0, 1.0);
}`;

export const MAP_FRAG = `#version 300 es
precision highp float;
precision highp int;
precision highp usampler2D;

// R = country id, G = HP clamped to u16, B = flags, A = reserved.
uniform usampler2D uBoard;
// 256x256 RGBA8, indexed by country id: x = id & 255, y = id >> 8.
uniform sampler2D uPalette;

uniform vec2  uViewport;    // device pixels
uniform vec2  uOrigin;      // board coordinate at the viewport's top-left
uniform float uScale;       // device pixels per board pixel
uniform vec2  uBoardSize;   // 320, 320

uniform vec3  uBackground;
uniform vec3  uGridColour;
uniform float uBaseHP;
uniform float uFortressHP;
uniform float uHoverPixel;  // -1 for none
uniform vec3  uHoverColour;
uniform float uValid;       // 0 while the board is discarded and awaiting a keyframe

const uint FLAG_FORTRESS = 1u;
const uint FLAG_DISPUTED = 8u;

out vec4 fragColour;

void main() {
  // gl_FragCoord is y-up from the bottom left; the board is y-down from the top
  // left, so the flip happens here, once, and nothing downstream thinks about it.
  vec2 device = vec2(gl_FragCoord.x, uViewport.y - gl_FragCoord.y);
  vec2 board = uOrigin + device / uScale;

  if (board.x < 0.0 || board.y < 0.0 || board.x >= uBoardSize.x || board.y >= uBoardSize.y) {
    fragColour = vec4(uBackground, 1.0);
    return;
  }

  ivec2 texel = ivec2(floor(board));
  uvec4 cell = texelFetch(uBoard, texel, 0);
  uint country = cell.r;
  float hp = float(cell.g);
  uint flags = cell.b;

  vec3 base = texelFetch(uPalette, ivec2(int(country) & 255, int(country) >> 8), 0).rgb;

  // Damage reads as damage: a pixel at 10% HP is visibly nearly gone, so a front
  // shows where it is about to move rather than only where it has already moved.
  float maxHP = (flags & FLAG_FORTRESS) != 0u ? uFortressHP : uBaseHP;
  float health = maxHP > 0.0 ? clamp(hp / maxHP, 0.0, 1.0) : 1.0;
  vec3 colour = mix(base * 0.22, base, 0.35 + 0.65 * health);

  // A fortress is a bought placement and must be legible as one at a glance.
  if ((flags & FLAG_FORTRESS) != 0u) {
    vec2 f = fract(board);
    float ring = step(0.18, f.x) * step(0.18, f.y) * step(f.x, 0.82) * step(f.y, 0.82);
    colour = mix(colour * 1.55 + 0.06, colour, ring);
  }

  // Disputed territory is neutral ON PURPOSE. Hatching says "left blank
  // deliberately" where a flat grey would just look unrendered.
  if ((flags & FLAG_DISPUTED) != 0u) {
    float hatch = step(0.5, fract((board.x + board.y) * 0.5));
    colour = mix(colour, colour * 0.55, hatch * 0.6);
  }

  // A hairline between pixels, and only once one board pixel is comfortably more
  // than one device pixel. Drawn any further out it is moire, not a grid.
  if (uScale >= 4.0) {
    vec2 f = fract(board);
    float edge = min(min(f.x, f.y), min(1.0 - f.x, 1.0 - f.y));
    float line = 1.0 - smoothstep(0.0, 1.0 / uScale, edge);
    colour = mix(colour, uGridColour, line * 0.35);
  }

  if (uHoverPixel >= 0.0) {
    float idx = float(texel.y) * uBoardSize.x + float(texel.x);
    if (abs(idx - uHoverPixel) < 0.5) colour = mix(colour, uHoverColour, 0.45);
  }

  // Gapped: the board has been discarded and the client is waiting for a keyframe.
  // Drained rather than blanked, because SPEC §3 accepts up to one keyframe
  // interval of staleness and a black screen every ten seconds would be worse
  // than a dimmed one.
  if (uValid < 0.5) {
    float grey = dot(colour, vec3(0.299, 0.587, 0.114));
    colour = mix(colour, vec3(grey), 0.75) * 0.55;
  }

  fragColour = vec4(colour, 1.0);
}`;

/**
 * The optimistic-click overlay: one instanced quad per click the player has made
 * and the server has not yet ruled on, plus the ones it has, animating out.
 *
 * The three failure outcomes are three different colours, three different shapes
 * and three different durations, which is the whole reason SELF carries a verdict
 * rather than a boolean. Collapse them and a player cannot tell an illegal move
 * from a lost one.
 */
export const OVERLAY_VERT = `#version 300 es
precision highp float;

layout(location = 0) in vec2 aCorner;    // unit quad, -1..1
layout(location = 1) in vec2 aPixel;     // board coordinate of the pixel's top-left
layout(location = 2) in float aState;    // OutcomeSlot
layout(location = 3) in float aAge;      // 0 at birth, 1 when it should be gone

uniform vec2  uViewport;
uniform vec2  uOrigin;
uniform float uScale;

out vec2  vLocal;
out float vState;
out float vAge;

void main() {
  vLocal = aCorner;
  vState = aState;
  vAge = aAge;

  // Markers grow with zoom but never below a size a finger can see: at world zoom
  // a single board pixel is under a device pixel, and a marker that honest is
  // invisible exactly when the player most needs the feedback.
  float halfPx = max(0.5, 3.0 / uScale) * (1.0 + 0.35 * aAge);
  vec2 centre = aPixel + vec2(0.5);
  vec2 board = centre + aCorner * halfPx;

  vec2 device = (board - uOrigin) * uScale;
  vec2 clip = vec2(device.x / uViewport.x * 2.0 - 1.0, 1.0 - device.y / uViewport.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
}`;

export const OVERLAY_FRAG = `#version 300 es
precision highp float;

in vec2  vLocal;
in float vState;
in float vAge;

// Indexed by OutcomeSlot: pending, applied, captured, illegal, lost, dropped.
uniform vec3 uColours[6];

out vec4 fragColour;

void main() {
  int slot = int(vState + 0.5);
  vec3 colour = uColours[clamp(slot, 0, 5)];
  float r = length(vLocal);

  float alpha;
  if (slot == 3) {
    // ILLEGAL — a hard double flash. It reads as a refusal, and the click is back
    // in the bank.
    float flash = step(0.35, fract(vAge * 4.0));
    alpha = (1.0 - vAge) * flash * (1.0 - smoothstep(0.55, 1.0, r));
  } else if (slot == 4) {
    // LOST — someone beat you to it. A ring that COLLAPSES inward: the snap-back,
    // and deliberately the opposite motion to everything else on screen.
    float ring = 1.0 - smoothstep(0.0, 0.28, abs(r - (1.0 - vAge) * 0.85));
    alpha = ring * (1.0 - vAge * 0.4);
  } else if (slot == 5) {
    // DROPPED — never reached the board, refunded. A dashed ring, so it reads as
    // "nothing happened here" rather than as a hit or a refusal.
    float ring = 1.0 - smoothstep(0.0, 0.22, abs(r - 0.8));
    float dash = step(0.5, fract(atan(vLocal.y, vLocal.x) * 1.9099));
    alpha = ring * dash * (1.0 - vAge);
  } else if (slot == 2) {
    // CAPTURED — the killing blow. Brighter, held longer, and it blooms outward.
    float bloom = 1.0 - smoothstep(0.0, 0.9 + vAge * 0.4, r);
    alpha = bloom * (1.0 - vAge * vAge);
  } else if (slot == 1) {
    // APPLIED — a small confirming tick. Quiet on purpose: most clicks land, and
    // a loud confirmation on every one of them is noise.
    alpha = (1.0 - smoothstep(0.25, 0.65, r)) * (1.0 - vAge);
  } else {
    // PENDING — drawn the instant the player clicks, before the server has said
    // anything. This is the frame that makes a 500 ms tick feel live.
    float ring = 1.0 - smoothstep(0.0, 0.3, abs(r - 0.72));
    alpha = ring * (0.55 + 0.45 * sin(vAge * 18.0));
  }

  if (alpha <= 0.004) discard;
  fragColour = vec4(colour * alpha, alpha);
}`;
